/**
 * ErrorAlert Component
 * Display error messages with dismiss and retry actions
 */

import React from 'react';
import { cn } from '../../utils';
import { Button } from './Button';

interface ErrorAlertProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  title = 'Error',
  message,
  onRetry,
  onDismiss,
  className,
}) => {
  return (
    <div
      className={cn(
        'bg-red-500/10 border border-red-500/30 rounded-lg p-4',
        className
      )}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 text-2xl">
          ❌
        </div>

        <div className="flex-1">
          <h4 className="text-red-400 font-semibold mb-1">
            {title}
          </h4>
          <p className="text-red-300 text-sm">
            {message}
          </p>

          {(onRetry || onDismiss) && (
            <div className="flex gap-2 mt-3">
              {onRetry && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={onRetry}
                >
                  Try Again
                </Button>
              )}
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                >
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </div>

        {onDismiss && !onRetry && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-red-400 hover:text-red-300 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

interface WarningAlertProps {
  title?: string;
  message: string;
  onDismiss?: () => void;
  className?: string;
}

export const WarningAlert: React.FC<WarningAlertProps> = ({
  title = 'Warning',
  message,
  onDismiss,
  className,
}) => {
  return (
    <div
      className={cn(
        'bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4',
        className
      )}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 text-2xl">
          ⚠️
        </div>

        <div className="flex-1">
          <h4 className="text-yellow-400 font-semibold mb-1">
            {title}
          </h4>
          <p className="text-yellow-300 text-sm">
            {message}
          </p>
        </div>

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

interface InfoAlertProps {
  title?: string;
  message: string;
  onDismiss?: () => void;
  className?: string;
}

export const InfoAlert: React.FC<InfoAlertProps> = ({
  title = 'Info',
  message,
  onDismiss,
  className,
}) => {
  return (
    <div
      className={cn(
        'bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4',
        className
      )}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 text-2xl">
          ℹ️
        </div>

        <div className="flex-1">
          <h4 className="text-cyan-400 font-semibold mb-1">
            {title}
          </h4>
          <p className="text-cyan-300 text-sm">
            {message}
          </p>
        </div>

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

interface SuccessAlertProps {
  title?: string;
  message: string;
  onDismiss?: () => void;
  className?: string;
}

export const SuccessAlert: React.FC<SuccessAlertProps> = ({
  title = 'Success',
  message,
  onDismiss,
  className,
}) => {
  return (
    <div
      className={cn(
        'bg-green-500/10 border border-green-500/30 rounded-lg p-4',
        className
      )}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 text-2xl">
          ✅
        </div>

        <div className="flex-1">
          <h4 className="text-green-400 font-semibold mb-1">
            {title}
          </h4>
          <p className="text-green-300 text-sm">
            {message}
          </p>
        </div>

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-green-400 hover:text-green-300 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};